import { API_CONFIG, createApiUrl } from '../api';
import { getUserIdFromToken, isTokenExpired } from './jwt-utils';

export interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  status: string;
  roles: string[];
  createdAt: string;
};

export interface Session {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user?: User;
};

export interface AuthResponse {
  success: boolean;
  message?: string;
  data?: any;
};

export interface LoginRequest {
  email: string;
  password: string;
};

export interface RegisterRequest {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  role?: string;
};

export interface RefreshRequest {
  refreshToken: string;
};

export interface LogoutRequest {
  refreshToken: string;
};

class ApiError extends Error {
  status: number;
  
  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

const makeRequest = async (url: string, options: RequestInit = {}): Promise<any> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.TIMEOUT);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorText = await response.text();
      throw new ApiError(response.status, errorText || `HTTP ${response.status}`);
    }

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    }
    
    return null;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof ApiError) {
      throw error;
    }
    throw new Error(error instanceof Error ? error.message : 'Network error');
  }
};

export const AuthAPI = {
  /**
   * Sign in with email and password
   */
  async signIn(email: string, password: string): Promise<Session> {
    const url = createApiUrl(API_CONFIG.ENDPOINTS.AUTH.LOGIN);
    const payload: LoginRequest = { email, password };
    
    const response = await makeRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    return {
      accessToken: response.accessToken,
      refreshToken: response.refreshToken,
      expiresIn: response.expiresIn,
    };
  },

  /**
   * Create new user account
   */
  async signUp(
    firstName: string,
    lastName: string,
    email: string,
    password: string
  ): Promise<Session> {
    const url = createApiUrl(API_CONFIG.ENDPOINTS.AUTH.REGISTER);
    const payload: RegisterRequest = { 
      firstName, 
      lastName, 
      email, 
      password,
      role: 'BUYER' // Default role
    };
    
    const response = await makeRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    return {
      accessToken: response.accessToken,
      refreshToken: response.refreshToken,
      expiresIn: response.expiresIn,
    };
  },

  /**
   * Send password reset email
   * NOTE: Password reset endpoint not implemented in backend yet
   */
  async sendPasswordReset(_email: string): Promise<void> {
    // TODO: Backend needs to implement password reset endpoint
    throw new Error('Password reset endpoint not implemented in backend yet');
  },

  /**
   * Reset password with token
   * NOTE: Password reset endpoint not implemented in backend yet
   */
  async resetPassword(_token: string, _password: string): Promise<void> {
    // TODO: Backend needs to implement password reset endpoint
    throw new Error('Password reset endpoint not implemented in backend yet');
  },

  /**
   * Sign out user
   */
  async signOut(refreshToken: string): Promise<void> {
    const url = createApiUrl(API_CONFIG.ENDPOINTS.AUTH.LOGOUT);
    const payload: LogoutRequest = { refreshToken };
    
    await makeRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  /**
   * Refresh access token
   */
  async refreshToken(refreshToken: string): Promise<Session> {
    const url = createApiUrl(API_CONFIG.ENDPOINTS.AUTH.REFRESH);
    const payload: RefreshRequest = { refreshToken };
    
    const response = await makeRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    return {
      accessToken: response.accessToken,
      refreshToken: response.refreshToken,
      expiresIn: response.expiresIn,
    };
  },

  /**
   * Resend email verification
   * NOTE: Email verification endpoint not implemented in backend yet
   */
  async resendVerificationEmail(_email: string): Promise<void> {
    // TODO: Backend needs to implement email verification endpoint
    throw new Error('Email verification endpoint not implemented in backend yet');
  },

  /**
   * Get current user profile
   */
  async getCurrentUser(accessToken: string): Promise<User> {
    const userId = getUserIdFromToken(accessToken);
    if (!userId) {
      throw new Error('Invalid access token');
    }

    const url = createApiUrl(API_CONFIG.ENDPOINTS.USER.PROFILE.replace('{userId}', userId));
    
    const response = await makeRequest(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    });

    return response;
  },

  /**
   * Check if access token needs refresh
   */
  isTokenExpired(accessToken: string): boolean {
    return isTokenExpired(accessToken);
  },

  /**
   * Google OAuth sign in
   * NOTE: Google OAuth endpoint not implemented in backend yet
   */
  async signInWithGoogle(_idToken: string): Promise<Session> {
    // TODO: Backend needs to implement Google OAuth endpoint
    throw new Error('Google OAuth endpoint not implemented in backend yet');
  },

  /**
   * Apple OAuth sign in
   * NOTE: Apple OAuth endpoint not implemented in backend yet
   */
  async signInWithApple(
    _identityToken: string,
    _user?: { name?: { firstName?: string; lastName?: string } }
  ): Promise<Session> {
    // TODO: Backend needs to implement Apple OAuth endpoint
    throw new Error('Apple OAuth endpoint not implemented in backend yet');
  },
};
